import React from 'react'
import { Flame, TrendingUp, Minus, TrendingDown, XCircle, HardDrive, Clock, Sparkles, Trash2 } from 'lucide-react'
import { tierOf } from '@/utils/advertiserTier'
import FireSparkline from '@/components/captured/FireSparkline'
import CapturedMedia from '@/components/captured/CapturedMedia'

export type Status = 'rising' | 'stable' | 'dying' | 'dead'

/**
 * Colores de estado validados contra la superficie stone-900 (#1c1917):
 * todos superan 4.5:1 de contraste. Nunca van solos — siempre con icono y etiqueta.
 */
export const STATUS: Record<Status, { label: string; color: string; Icon: typeof TrendingUp; help: string }> = {
  rising: { label: 'Subiendo', color: '#34d399', Icon: TrendingUp, help: 'Gana copias cada día' },
  stable: { label: 'Estable', color: '#a8a29e', Icon: Minus, help: 'Se mantiene sin crecer' },
  dying: { label: 'Cayendo', color: '#fbbf24', Icon: TrendingDown, help: 'Está perdiendo copias' },
  dead: { label: 'Muerto', color: '#fb7185', Icon: XCircle, help: 'Ya no está activo' }
}

export const TRIGGER_LABEL: Record<string, string> = {
  volume: 'Volumen',
  velocity: 'Velocidad',
  longevity: 'Longevidad',
  leader: 'Líder del nicho',
  direct: 'Respuesta directa'
}

/**
 * Hace cuánto se confirmó por última vez que este anuncio sigue corriendo.
 *
 * Importa porque las búsquedas piden solo anuncios activos: uno que el anunciante apaga
 * deja de aparecer sin decir que se apagó. El minero lo comprueba cada pocos días, pero
 * entre comprobación y comprobación la tarjeta debe decir de cuándo es el dato en vez de
 * presentarlo como si fuera de ahora.
 */
function Frescura({ lastSeenAt }: { lastSeenAt?: string }) {
  if (!lastSeenAt) return null
  const horas = (Date.now() - new Date(lastSeenAt).getTime()) / 3_600_000
  if (!Number.isFinite(horas) || horas < 36) return null

  const dias = Math.round(horas / 24)
  const viejo = dias >= 5
  return (
    <span
      className="flex items-center gap-1 text-xs"
      style={{ color: viejo ? '#fbbf24' : '#78716c' }}
      title={
        viejo
          ? 'Lleva días sin confirmarse. El minero lo comprobará: si lo apagaron, pasará a Muerto.'
          : 'Última vez que se confirmó que sigue corriendo'
      }
    >
      visto hace {dias}d
    </span>
  )
}

/**
 * Una línea de la fórmula. Se omite sola cuando el modelo no encontró ese elemento, para
 * que una oferta sin mecanismo claro no enseñe una fila vacía.
 */
function FormulaRow({ etiqueta, valor }: { etiqueta: string; valor?: string | null }) {
  if (!valor || valor === '—') return null
  return (
    <div className="flex gap-2">
      <dt className="w-20 shrink-0 text-stone-500">{etiqueta}</dt>
      <dd className="min-w-0 flex-1 text-stone-300">{valor}</dd>
    </div>
  )
}

/**
 * La tarjeta de una captura. La usan Capturas y Mis anuncios, para que un anuncio se vea
 * igual en las dos; cada página pone sus botones en `footer`.
 */
/**
 * Si el anunciante sube o baja: sus dos últimos recuentos de días distintos, contados igual.
 * Por debajo de ±20 % (o de 3 anuncios) es ruido de un día y se enseña como estable.
 */
function TendenciaAnunciante({ trend }: { trend?: { antes: number; hoy: number; antesDia: string; hoyDia: string } }) {
  if (!trend) return null
  const { antes, hoy, antesDia, hoyDia } = trend
  const d = hoy - antes
  const pct = antes > 0 ? Math.round((100 * d) / antes) : 0
  const dia = (s: string) => s.slice(8, 10) + '/' + s.slice(5, 7)
  const title = `${antes} → ${hoy} anuncios activos entre el ${dia(antesDia)} y el ${dia(hoyDia)}`
  const [texto, color, bg, Icono] =
    hoy === 0
      ? (['apagó', '#fca5a5', 'rgba(239,68,68,0.12)', TrendingDown] as const)
      : d >= 3 && pct >= 20
        ? ([`+${d} (${pct > 999 ? '999+' : pct}%)`, '#86efac', 'rgba(34,197,94,0.12)', TrendingUp] as const)
        : d <= -3 && pct <= -20
          ? ([`${d} (${pct}%)`, '#fca5a5', 'rgba(239,68,68,0.12)', TrendingDown] as const)
          : (['estable', '#a8a29e', 'rgba(168,162,158,0.10)', Minus] as const)
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums"
      style={{ color, background: bg }}
      title={title}
      aria-label={title}
    >
      <Icono className="h-3.5 w-3.5" aria-hidden />
      {texto}
    </span>
  )
}

export default function CapturedAdCard({ item, footer }: { item: any; footer?: React.ReactNode }) {
  // Sin estado solo llega un guardado desde la búsqueda, que nunca se siguió: mejor no
  // decir nada que inventarle un "Estable".
  const st = item.status ? STATUS[item.status as Status] || STATUS.stable : null
  const ad = item.adData || {}
  const media = ad.apify_data || {}
  const profilePic = media.page_profile_picture_url
  const hook = ad.ad_creative_bodies?.[0] || ad.ad_creative_link_titles?.[0] || ''
  const velocity = item.metrics?.scalingVelocity ?? 0
  const copies = item.metrics?.currentCollationCount ?? 0
  const peak = item.peak?.collationCount ?? 0

  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-stone-700 bg-stone-900">
      {/* El creativo manda: es lo que vienes a ver. Vídeo reproducible en la propia
          tarjeta, imagen sin recortar, y flechas si el anuncio trae varios. */}
      <CapturedMedia ad={ad} originalMedia={item.originalMedia} heightClass="h-56" />

      <div className="flex items-start gap-3 p-4 pb-3">
        {profilePic ? (
          <img
            src={profilePic}
            alt=""
            className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-stone-800 text-stone-600">
            <Flame className="h-4 w-4" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <h3 className="truncate font-semibold text-stone-100" title={item.pageName}>
              {item.pageName || 'Anunciante desconocido'}
            </h3>
            {/* Lo capturado en las últimas 24 h, marcado en la propia tarjeta: con
                370 tarjetas, saber qué llegó anoche sin cambiar de filtro. */}
            {Date.now() - new Date(item.firstCapturedAt).getTime() < 86_400_000 && (
              <span
                className="flex-shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-xs font-medium text-amber-400"
                title={`Capturada ${new Date(item.firstCapturedAt).toLocaleString('es-ES')}`}
              >
                nueva
              </span>
            )}
            {ad.ligera && (
              <span
                className="flex-shrink-0 rounded-full bg-stone-800 px-1.5 py-0.5 text-xs text-stone-500"
                title="Descartada: se conserva la ficha (nombre, texto, miniatura y destino), no el creativo entero. El enlace a la Ad Library sigue abajo."
              >
                ficha
              </span>
            )}
            {item.siblingCount > 1 && (
              <span
                className="flex-shrink-0 rounded-full bg-stone-800 px-1.5 py-0.5 text-xs text-stone-400"
                title={`Este anunciante tiene ${item.siblingCount} creativos capturados. Se muestra el mejor.`}
              >
                +{item.siblingCount - 1}
              </span>
            )}
          </div>
          {st && (
            <div className="mt-1 flex items-center gap-1.5 text-xs" style={{ color: st.color }}>
              <st.Icon className="h-3.5 w-3.5" />
              <span className="font-medium">{st.label}</span>
              <span className="text-stone-500">· {st.help}</span>
            </div>
          )}
        </div>

        {item.media?.archived && (
          <HardDrive className="h-4 w-4 flex-shrink-0 text-stone-500" aria-label="Media archivada" />
        )}
      </div>

      {hook && (
        <div className="px-4 pb-3">
          {/* El recorte va con alto exacto y overflow, no solo con line-clamp: en esta
              app `display: -webkit-box` acaba pisado a `flow-root` y el texto se
              desbordaba sobre la fila de abajo. Dos líneas de 1.25rem = 2.5rem. */}
          <p
            className="overflow-hidden text-sm text-stone-300"
            title={hook}
            style={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              lineHeight: '1.25rem',
              maxHeight: '2.5rem'
            }}
          >
            {hook}
          </p>
        </div>
      )}

      {/* Dos números distintos y los dos importan: las COPIAS dicen cuántas veces
          repite ESTE creativo, y los ANUNCIOS ACTIVOS dicen el tamaño de toda su
          operación. Un anunciante puede repetir un creativo diez veces (apuesta
          concentrada) o correr cien distintos (máquina montada), y las copias solas
          no distinguen los dos casos. */}
      <div className="flex items-end gap-3 px-4 pb-2">
        <div className="flex-shrink-0">
          <div className="flex items-baseline gap-1.5">
            <span
              className="text-4xl font-bold leading-none tabular-nums"
              style={{ color: copies >= 5 ? '#e879f9' : '#f5f5f4' }}
            >
              {copies}
            </span>
            {peak > copies && (
              <span className="text-xs text-stone-500" title={`Llegó a ${peak} copias`}>
                de {peak}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs uppercase tracking-wide text-stone-500">
            {copies === 1 ? 'copia activa' : 'copias activas'}
            {item.standout?.ratio >= 2 && (
              <span
                className="ml-1.5 rounded px-1.5 py-0.5 text-xs font-medium"
                style={{ background: '#a78bfa1a', color: '#a78bfa' }}
                title={`Su mercado corre ${item.standout.marketMedian} ${
                  item.standout.marketMedian === 1 ? 'copia' : 'copias'
                } de mediana y el que más tiene llega a ${item.standout.marketMax}. Este va ${item.standout.ratio}× por encima de la mediana.`}
              >
                ×{item.standout.ratio} su nicho
              </span>
            )}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <FireSparkline timeline={item.timeline || []} height={44} />
        </div>
      </div>

      {/* El tamaño del anunciante. Se omite si aún no se sabe, en vez de enseñar un
          cero: un anunciante del que hay una captura viva tiene al menos un anuncio,
          así que un cero solo puede significar "no lo hemos consultado". */}
      {item.advertiser?.activeAds > 0 && (
        <div className="flex items-center gap-2 px-4 pb-2">
          <span className="text-2xl font-bold leading-none tabular-nums text-stone-100">
            {item.advertiser.activeAds}
          </span>
          <span className="text-xs uppercase tracking-wide text-stone-500">
            anuncios activos
            <br />
            del anunciante
          </span>
          <TendenciaAnunciante trend={item.advertiserTrend} />
          {(() => {
            const t = tierOf(item.advertiser.activeAds)
            if (!t) return null
            return (
              <span
                className="ml-auto rounded-full px-2.5 py-1 text-xs font-semibold"
                style={{ background: t.bg, color: t.color, border: `1px solid ${t.border}` }}
                title={`${item.advertiser.activeAds} anuncios activos en total. Lo que copies de aquí ya lo validó un presupuesto.`}
              >
                {t.label}
              </span>
            )
          })()}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 px-4 py-3 text-center">
        <div>
          <div className="text-sm font-semibold text-stone-100">
            {velocity > 0 ? '+' : ''}
            {velocity}
          </div>
          <div className="text-xs text-stone-500">copias/día</div>
        </div>
        <div>
          <div className="text-sm font-semibold text-stone-100">
            {item.metrics?.currentDaysRunning ?? 0}d
          </div>
          <div className="text-xs text-stone-500">activo</div>
        </div>
        <div>
          <div className="text-sm font-semibold text-stone-100">
            {item.metrics?.daysTracked ?? 0}d
          </div>
          <div className="text-xs text-stone-500">siguiéndolo</div>
        </div>
      </div>

      {/* La fórmula de la oferta, sacada del guion hablado del vídeo. Solo la tienen
          los anuncios que más escalan: es la parte que cuesta dinero analizar, y
          es justo lo que no se puede leer en el texto del anuncio. */}
      {item.intelligence && (
        <div className="mx-4 mb-3 rounded-lg border border-stone-800 bg-stone-900/60 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-amber-400">
            <Sparkles className="h-3.5 w-3.5" />
            La oferta
            <span
              className="ml-auto font-normal text-stone-600"
              title={
                item.intelligence.fuente === 'transcripcion'
                  ? 'Extraído del guion hablado del vídeo'
                  : 'Extraído del texto del anuncio: este no tenía vídeo con transcripción'
              }
            >
              {item.intelligence.fuente === 'transcripcion' ? 'del vídeo' : 'del texto'}
            </span>
          </div>

          <p className="text-sm font-medium leading-snug text-stone-100">
            {item.intelligence.oferta}
          </p>

          <dl className="mt-2 space-y-1.5 text-xs">
            <FormulaRow etiqueta="Promete" valor={item.intelligence.promesa} />
            <FormulaRow etiqueta="Abre con" valor={item.intelligence.gancho} />
            <FormulaRow etiqueta="Mecanismo" valor={item.intelligence.mecanismo} />
            <FormulaRow etiqueta="Le habla a" valor={item.intelligence.publico} />
            <FormulaRow etiqueta="Prueba" valor={item.intelligence.prueba} />
            <FormulaRow etiqueta="Urgencia" valor={item.intelligence.urgencia} />
          </dl>

          {item.intelligence.precio && (
            <div className="mt-2 inline-block rounded bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-400">
              {item.intelligence.precio}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-1 px-4 pb-3">
        {item.classification && (
          <span
            className="rounded-full px-2 py-0.5 text-xs"
            style={
              item.classification.isDigitalProduct
                ? { background: '#e879f91a', color: '#e879f9', border: '1px solid #e879f94d' }
                : { background: '#fb71851a', color: '#fb7185', border: '1px solid #fb71854d' }
            }
            title={`${item.classification.reason} · ${Math.round(
              item.classification.confidence * 100
            )}% de confianza · ${item.classification.source === 'ai' ? 'analizado con IA' : 'por reglas'}`}
          >
            {item.classification.kind}
          </span>
        )}
        {(item.triggers || []).map((t: string) => (
          <span
            key={t}
            className="rounded-full border border-stone-700 px-2 py-0.5 text-xs text-stone-400"
          >
            {TRIGGER_LABEL[t] || t}
          </span>
        ))}
        <span className="ml-auto flex items-center gap-2 text-xs text-stone-600">
          {/* En la papelera manda la fecha de rechazo: es por lo que se ordena y lo
              que dice si este es el que tiraste sin querer hace un rato. */}
          {item.dismissedAt ? (
            <span
              className="flex items-center gap-1 text-rose-400/80"
              title={
                item.dismissedBy === 'clasificador'
                  ? 'Descarte automático: el clasificador o una purga'
                  : 'Lo descartaste tú desde la rejilla'
              }
            >
              <Trash2 className="h-3 w-3" />
              {new Date(item.dismissedAt).toLocaleString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
              {item.dismissedBy === 'clasificador' && ' · auto'}
            </span>
          ) : (
            <Frescura lastSeenAt={item.lastSeenAt} />
          )}
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {new Date(item.firstCapturedAt).toLocaleDateString('es-ES')}
          </span>
        </span>
      </div>

      {footer}
    </article>
  )
}
